import React, { useEffect, useState } from 'react';
import IApplicationReview from 'interfaces/IApplicationReview';
import IApplicationReviewForm, { IPartData } from 'interfaces/IApplicationReviewForm';
import useApplicationReview from './useApplicationReview';
import SingleInput from '../Steps/SingleInput';
import AccordionInput from '../Steps/AccordionInput';
import PartD from '../Steps/PartD';
import Confirmation from '../Steps/Confirmation';
import ISimpleNameValuePair from 'interfaces/ISimpleNameValuePair';
import {
    IAccreditationApplicationRequirements,
    IEvaluationRequirementsGroup,
    IReaccreditationApplicationRequirements,
} from 'interfaces/IApplicationRequirements';
import { ReviewFormat, ReviewStatus } from 'graphql/__generated__/graphql';
import { IStepConfig } from '../../../Form/FormStepper/FormStepper';
import IRankingsPair from '../../../../interfaces/IRankingsPair';
import { useNavigate } from 'react-router-dom';
import useAlgorithmCalculator from './useAlgorithmCalculator';

const emptyRequirements = { config: { help: {} }, evaluation: [], experience: [], documents: [] };

function reviewToForm(applicationReview: IApplicationReview): IApplicationReviewForm {
    const { applicationReviewId, jsonData } = applicationReview;
    const appData = JSON.parse(jsonData || '{}');

    return {
        applicationReviewId: applicationReviewId,
        ...appData,
    };
}

interface OperationResult {
    success: boolean;
    message: string | string[];
}

export const useSupervisorReviewFormComponent = (
    applicationReviewSummary: IApplicationReview,
    readOnly = false
) => {
    const navigate = useNavigate();
    const { models, operations } = useApplicationReview(applicationReviewSummary);
    const { calculateScore } = useAlgorithmCalculator();

    const { update } = operations;
    const { loading } = models;

    // Application form data
    // const [applicationReview, setApplicationReview] = useState<IApplicationReview>();
    const [formData, setFormData] = useState<IApplicationReviewForm>(
        reviewToForm(models.applicationReview)
    );
    const [requirements, setRequirements] = useState<
        IAccreditationApplicationRequirements | IReaccreditationApplicationRequirements
    >(emptyRequirements);
    const [stepsConfig, setStepsConfig] = useState<IStepConfig[]>([]);
    const [validated, setValidated] = useState<boolean>(false);
    const [operationResult, setOperationResult] = useState<OperationResult>();

    // Form visibility
    const [updating, setUpdating] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        if (
            models.applicationReview?.accreditationApplication?.accreditationType
                .requirementsJson &&
            !loading
        ) {
            const reqJson = JSON.parse(
                models.applicationReview?.accreditationApplication?.accreditationType
                    ?.requirementsJson || '{}'
            );
            const specificRequirements = applicationReviewSummary.isRenewal
                ? reqJson.reaccreditation
                : reqJson.accreditation;
            setRequirements(specificRequirements ?? reqJson);
            setFormData(reviewToForm(models.applicationReview));
        }
    }, [models.applicationReview, loading]);

    const determineContentType = (evalGroup: IEvaluationRequirementsGroup) => {
        switch ((evalGroup.contentType || '').toLowerCase()) {
            case 'accordion':
                return AccordionInput;
            case 'singleinput':
                return SingleInput;
            case 'partd':
                return PartD;
            default:
                return evalGroup.requirements.length > 1 ? AccordionInput : SingleInput;
        }
    };

    useEffect(() => {
        if (requirements) {
            const newStepsConfig: IStepConfig[] = (requirements?.evaluation || []).map((req) => {
                return {
                    title: req.title,
                    content: determineContentType(req),
                    helpId: req.name,
                };
            });

            setStepsConfig([
                ...newStepsConfig,
                {
                    title: 'Confirmation',
                    showNextButton: false,
                    showSaveProgressButton: false,
                    content: Confirmation,
                },
            ]);
        }
    }, [requirements]);

    const handleSaveProgress = async () => {
        if (!formData || updating || readOnly) return;

        setUpdating(true);

        const updatedReview: IApplicationReview = {
            ...models.applicationReview,
            jsonData: JSON.stringify(formData),
            reviewStatus: ReviewStatus.InProgress,
        };

        return update(updatedReview)
            .then(() => {
                setOperationResult({
                    success: true,
                    message: 'Saved successfully',
                });
            })
            .catch((error) => {
                console.error('An error occurred saving the review', error);
                setOperationResult({
                    success: false,
                    message: 'An error occurred saving.',
                });
            })
            .finally(() => {
                setUpdating(false);
            });
    };

    const handleInputChangeEvent = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ): void => {
        const { name, value } = event.target;
        handleInputChange(name, value);
    };

    const handleInputChange = (
        name: string,
        value:
            | string
            | string[]
            | boolean
            | ISimpleNameValuePair[]
            | IPartData<unknown>
            | IRankingsPair
    ): void => {
        const updatedFormData = {
            ...formData,
            [name]: value,
        };

        if (models.applicationReview?.reviewFormat === ReviewFormat.Algorithm) {
            updatedFormData.score = calculateScore(updatedFormData);
        }

        setFormData(updatedFormData);
    };

    const handleSubmit = (reviewStatus?: ReviewStatus) => {
        const rs = reviewStatus ?? ReviewStatus.Complete;
        update({
            ...models.applicationReview,
            jsonData: JSON.stringify(formData),
            reviewStatus: rs,
        })
            .then((response) => {
                navigate(
                    '/accreditation?formSubmitType=evaluation&formSubmitResult=' + rs.toLowerCase()
                );
            })
            .catch((error) => {
                console.error('error', error);
                return false;
            })
            .finally(() => {
                setUpdating(false);
            });
    };

    const handleSetReviewFormat = (reviewFormat: ReviewFormat) => {
        setUpdating(true);
        update({
            jsonData: JSON.stringify(formData),
            ...models.applicationReview,
            reviewFormat,
        }).finally(() => {
            setUpdating(false);
        });
    };

    const handleNextStep = (back?: boolean) => {
        return handleSetStep(
            back ? Math.max(currentStep - 1, 1) : Math.min(currentStep + 1, stepsConfig.length)
        );
    };

    const handleSetStep = (step: number) => {
        setValidated(false);
        handleSaveProgress().then(() => {
            if (step > currentStep) {
                const sc = stepsConfig[currentStep - 1];
                if (sc.validate) {
                    const errors = sc.validate();
                    if (errors.length) {
                        setOperationResult({
                            success: false,
                            message: errors,
                        });
                        setValidated(true);
                        return;
                    }
                }
            }
            setValidated(false);
            setCurrentStep(step);
        });
    };

    return {
        models: {
            formData,
            currentStep,
            updating,
            stepsConfig,
            applicationReview: models.applicationReview,
            loading: models.loading,
            requirements,
            validated,
            operationResult,
        },
        operations: {
            handleInputChangeEvent,
            handleInputChange,
            handleNextStep,
            handleSaveProgress,
            handleSetStep,
            handleSubmit,
            handleSetReviewFormat,
        },
    };
};
